import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useCustomLoginWithRedirect from "./useCustomLoginWithRedirect";
import { useUser } from "@/context/UserContext";

interface UseOneTapAuthParams {
    redirectPath?: string;
    version?: string;
    onClose?: () => void;
    location?: string;
}

export const useOneTapAuth = ({
    redirectPath = "/puzzle",
    version = "puzzle",
    onClose,
    location = "",
}: UseOneTapAuthParams) => {
    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const { customLoginWithRedirect } = useCustomLoginWithRedirect();

    const isUserCreated = useMemo(() => !!userState?.is_created, [userState?.is_created]);
    const shouldShow = useMemo(() => !(isAuthenticated || isUserCreated), [isAuthenticated, isUserCreated]);
    const hasClosedRef = useRef(false);

    useEffect(() => {
        if (!onClose) return;
        // Only trigger onClose once, when we transition from a state where the prompt
        // could be shown to a state where it should no longer be shown.
        if (!shouldShow && !hasClosedRef.current) {
            hasClosedRef.current = true;
            onClose();
        }
    }, [onClose, shouldShow]);

    const handleGoogle = useCallback(() => {
        customLoginWithRedirect({
            authorizationParams: {
                redirect_uri: window.location.origin,
                connection: "google-oauth2",
            },
            appState: { version, location, returnTo: redirectPath },
        });
    }, [customLoginWithRedirect, redirectPath, version, location]);

    const handleEmail = useCallback(() => {
        customLoginWithRedirect({
            authorizationParams: {
                redirect_uri: window.location.origin,
                screen_hint: "signup",
            },
            appState: { version, location, returnTo: redirectPath },
        });
    }, [customLoginWithRedirect, redirectPath, version, location]);

    return {
        shouldShow,
        handleGoogle,
        handleEmail,
    };
};

export default useOneTapAuth;
